import React from 'react'
import { CheckCircleOutlined, ErrorOutlineOutlined, ReportProblemOutlined, InfoOutlined } from '@mui/icons-material'
import { createTheme } from '@mui/material/styles'
import colors from './colors'

export enum FONT_WEIGHT {
  light = 300,
  normal = 400,
  medium = 500
}

declare module '@mui/material/styles' {
  interface PaletteColor {
    containedHoverBackground?: string
    outlinedHoverBackground?: string
    outlinedRestingBackground?: string
    content?: string
    background?: string
  }

  interface SimplePaletteColorOptions {
    containedHoverBackground?: string
    outlinedHoverBackground?: string
    outlinedRestingBackground?: string
    content?: string
    background?: string
  }
}

const systemFonts = [
  '-apple-system',
  'BlinkMacSystemFont',
  'Segoe UI',
  'Roboto',
  'Helvetica Neue',
  'Arial',
  'sans-serif',
  'Apple Color Emoji',
  'Segoe UI Emoji',
  'Segoe UI Symbol'
].join(',')

const fonts = {
  system: systemFonts,
  body: `'Poppins', ${systemFonts}`
}

const themeBase = createTheme({
  components: {
    MuiUseMediaQuery: {
      defaultProps: {
        noSsr: true
      }
    },
    MuiButton: {
      styleOverrides: {
        root: {
          fontSize: '13px',
          fontWeight: FONT_WEIGHT.medium,
          lineHeight: '24px',
          letterSpacing: '1.25px',
          textTransform: 'capitalize'
        },
        sizeLarge: {
          fontSize: '14px',
          fontWeight: FONT_WEIGHT.medium,
          lineHeight: '26px',
          letterSpacing: '1.25px'
        },
        sizeSmall: {
          fontSize: '13px',
          fontWeight: FONT_WEIGHT.medium,
          lineHeight: '22px',
          letterSpacing: '1.25px'
        }
      }
    },
    MuiAvatar: {
      styleOverrides: {
        root: {
          letterSpacing: '0.14px'
        }
      }
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          fontSize: '10px',
          fontWeight: FONT_WEIGHT.medium,
          lineHeight: '14px',
          letterSpacing: 0
        }
      }
    }
  },
  typography: {
    fontFamily: fonts.body,
    h1: {
      fontSize: '93px',
      fontWeight: FONT_WEIGHT.light,
      lineHeight: '112px',
      letterSpacing: '-1.5px'
    },
    h2: {
      fontSize: '58px',
      fontWeight: FONT_WEIGHT.light,
      lineHeight: '120%',
      letterSpacing: '-0.5px'
    },
    h3: {
      fontSize: '46px',
      fontWeight: FONT_WEIGHT.normal,
      lineHeight: '116.7%',
      letterSpacing: 0
    },
    h4: {
      fontSize: '33px',
      fontWeight: FONT_WEIGHT.normal,
      lineHeight: '123.5%',
      letterSpacing: '0.25px'
    },
    h5: {
      fontSize: '23px',
      fontWeight: FONT_WEIGHT.normal,
      lineHeight: '133.4%',
      letterSpacing: 0
    },
    h6: {
      fontSize: '19px',
      fontWeight: FONT_WEIGHT.medium,
      lineHeight: '160%',
      letterSpacing: '0.15px'
    },
    subtitle1: {
      fontSize: '15px',
      fontWeight: FONT_WEIGHT.normal,
      lineHeight: '175%',
      letterSpacing: '0.15px'
    },
    subtitle2: {
      fontSize: '13px',
      fontWeight: FONT_WEIGHT.medium,
      lineHeight: '157%',
      letterSpacing: '0.1px'
    },
    body1: {
      fontSize: '15px',
      fontWeight: FONT_WEIGHT.normal,
      lineHeight: '150%',
      letterSpacing: '0.5px'
    },
    body2: {
      fontSize: '13px',
      fontWeight: FONT_WEIGHT.normal,
      lineHeight: '143%',
      letterSpacing: '0.25px'
    },
    caption: {
      fontSize: '12px',
      fontWeight: FONT_WEIGHT.normal,
      lineHeight: '166%',
      letterSpacing: '0.4px'
    },
    overline: {
      fontSize: '10px',
      fontWeight: FONT_WEIGHT.normal,
      lineHeight: '266%',
      letterSpacing: '1.5px',
      textTransform: 'uppercase'
    }
  },
  palette: {
    primary: {
      ...colors.primary,
      main: colors.primary[500],
      dark: colors.primary[700],
      light: colors.primary[300]
    },
    secondary: {
      ...colors.secondary,
      main: colors.secondary[500],
      dark: colors.secondary[700],
      light: colors.secondary[300]
    },
    info: {
      ...colors.info,
      main: colors.info[500],
      dark: colors.info[700],
      light: colors.info[300]
    },
    warning: {
      ...colors.warning,
      main: colors.warning[500],
      dark: colors.warning[700],
      light: colors.warning[300]
    },
    success: {
      ...colors.success,
      main: colors.success[500],
      dark: colors.success[700],
      light: colors.success[300]
    },
    error: {
      ...colors.error,
      main: colors.error[500],
      dark: colors.error[700],
      light: colors.error[300]
    },
    grey: colors.grey,
    text: {
      primary: colors.black,
      secondary: '#737491DE',
      disabled: '#4A4B6561'
    },
    background: {
      paper: colors.white,
      default: '#F1F5F9'
    },
    action: {
      active: '#4A4B6599',
      hover: '#4A4B650A',
      selected: '#4A4B6514',
      disabled: '#4A4B6542',
      disabledBackground: '#4A4B651F',
      focus: '#4A4B651F'
    }
  },
  breakpoints: {
    values: {
      xs: 0,
      // width < 744: mobile, margin: 16
      sm: 744,
      // width > 744, <1200: tablet, margin: 16
      md: 1200,
      // width > 1200, <1440: desktop, margin: 16
      lg: 1440,
      // width > 1440: desktop, margin: 24
      xl: 1920
    }
  },
  spacing: 4
})

const getStardardBackground = (color: string) => {
  return `linear-gradient(0deg, rgba(255, 255, 255, 0.9), rgba(255, 255, 255, 0.9)), ${color}`
}

const theme = createTheme(themeBase, {
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          fontFamily: fonts.body,
          backgroundColor: '#F1F5F9'
        },
        a: {
          textDecoration: 'none',
          color: 'inherit'
        }
      }
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: themeBase.spacing(4)
        }
      }
    },
    MuiPaper: {
      styleOverrides: {
        rounded: {
          borderRadius: themeBase.spacing(4),
          borderColor: '#4A4B651F',
          boxShadow: '0px 0px 0px 1px #E0E0E0'
        }
      }
    },
    MuiCard: {
      styleOverrides: {
        root: {
          width: '100%',
          height: 'auto',
          borderRadius: themeBase.spacing(4)
        }
      }
    },
    MuiCardContent: {
      styleOverrides: {
        root: {
          padding: themeBase.spacing(4),
          '&:last-child': {
            paddingBottom: themeBase.spacing(4)
          }
        }
      }
    },
    MuiList: {
      styleOverrides: {
        padding: {
          paddingTop: 0,
          paddingBottom: 0
        }
      }
    },
    MuiButton: {
      styleOverrides: {
        sizeSmall: {
          borderRadius: themeBase.spacing(3.75)
        },
        sizeMedium: {
          borderRadius: themeBase.spacing(4.5)
        },
        sizeLarge: {
          borderRadius: themeBase.spacing(6)
        }
      }
    },
    MuiButtonGroup: {
      styleOverrides: {
        root: {
          borderRadius: themeBase.spacing(6)
        }
      }
    },
    MuiChip: {
      styleOverrides: {
        colorDefault: {
          borderColor: '#0000003b'
        }
      }
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          color: colors.white
        }
      }
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          padding: `${themeBase.spacing(1.25)} ${themeBase.spacing(4)}`,
          borderRadius: themeBase.spacing(6),
          boxShadow: 'none',
          '.MuiAlert-message': {
            paddingTop: `${themeBase.spacing(2.42625)}`,
            paddingBottom: `${themeBase.spacing(2.42625)}`
          },
          '.MuiAlert-icon': {
            opacity: 1
          },
          '.MuiAlert-action': {
            color: 'inherit',
            '.MuiButton-root': {
              color: 'inherit'
            },
            '.MuiIconButton-root': {
              color: 'inherit'
            }
          }
        },
        filled: {
          color: colors.white,
          '.MuiAlert-icon': {
            color: colors.white
          },
          '.MuiButton-outlined': {
            borderColor: colors.white,
            '&:hover': {
              borderColor: colors.white
            }
          }
        },
        outlinedError: {
          color: '#61112C',
          borderColor: colors.error[500],
          '.MuiAlert-icon': {
            color: colors.error[500]
          },
          '.MuiButton-outlined': {
            borderColor: '#F995B6'
          }
        },
        standardError: {
          color: '#61112C',
          borderColor: colors.error[500],
          background: getStardardBackground(colors.error[500]),
          '.MuiAlert-icon': {
            color: colors.error[500]
          },
          '.MuiButton-outlined': {
            borderColor: '#61112C'
          }
        },
        filledError: {
          backgroundColor: colors.error[500]
        },
        outlinedWarning: {
          color: '#612C10',
          borderColor: colors.warning[500],
          '.MuiAlert-icon': {
            color: colors.warning[500]
          },
          '.MuiButton-outlined': {
            borderColor: '#F9B693'
          }
        },
        standardWarning: {
          color: '#612C10',
          borderColor: colors.warning[500],
          background: getStardardBackground(colors.warning[500]),
          '.MuiAlert-icon': {
            color: colors.warning[500]
          },
          '.MuiButton-outlined': {
            borderColor: '#612C10'
          }
        },
        outlinedInfo: {
          color: '#452965',
          borderColor: colors.info[500],
          '.MuiAlert-icon': {
            color: colors.info[500]
          },
          '.MuiButton-outlined': {
            borderColor: '#D5B3FE'
          }
        },
        standardInfo: {
          color: '#452965',
          borderColor: colors.info[500],
          background: getStardardBackground(colors.info[500]),
          '.MuiAlert-icon': {
            color: colors.info[500]
          },
          '.MuiButton-outlined': {
            borderColor: '#452965'
          }
        },
        outlinedSuccess: {
          color: '#0d4b42',
          borderColor: colors.success[500],
          '.MuiAlert-icon': {
            color: colors.success[500]
          },
          '.MuiButton-outlined': {
            borderColor: '#90DDD2'
          }
        },
        standardSuccess: {
          color: '#0d4b42',
          background: getStardardBackground(colors.success[500]),
          '.MuiAlert-icon': {
            color: colors.success[500]
          },
          '.MuiButton-outlined': {
            borderColor: '#0d4b42'
          }
        },
        filledSuccess: {
          backgroundColor: colors.success[500]
        }
      },
      defaultProps: {
        iconMapping: {
          success: <CheckCircleOutlined />,
          error: <ErrorOutlineOutlined />,
          warning: <ReportProblemOutlined />,
          info: <InfoOutlined />
        }
      }
    },
    MuiSnackbar: {
      styleOverrides: {
        root: {
          zIndex: themeBase.zIndex.appBar + 1,
          '.MuiSnackbarContent-action .MuiButton-root': {
            color: colors.white
          },
          '.MuiIconButton-root': {
            color: colors.white
          }
        }
      }
    }
  }
})

export default theme
